import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, CameraOff, CheckCircle2, AlertTriangle, RefreshCw, Users, ShieldCheck, Radio } from "lucide-react";

// Saal-Kontrolle: Beim Filmstart per Handy jedes Ticket im Saal scannen –
// live sehen, welche verkauften Sitze noch nicht bestätigt sind.
// Live-Sync via WebSocket: Scans erscheinen sofort auf allen Handys.
export default function SaalKontrolle() {
  const [showtimes, setShowtimes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wsStatus, setWsStatus] = useState("off"); // off | connecting | live | fallback
  const [peers, setPeers] = useState(0);
  const scannerRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const reload = React.useCallback(() => {
    if (!selectedId) return;
    api.get(`/admin/showtimes/${selectedId}/audit`).then((r) => setData(r.data)).catch(() => {});
  }, [selectedId]);

  // Optimistisches Merge eines Live-Scan-Events aus WebSocket
  const applyLiveScan = React.useCallback((ev) => {
    setData((cur) => {
      if (!cur) return cur;
      const tickets = cur.tickets.map((t) => t.id === ev.ticket_id
        ? { ...t, audit_verified_at: ev.audit_verified_at, audit_verified_by: ev.audit_verified_by }
        : t);
      const audit_verified = tickets.filter((t) => t.audit_verified_at).length;
      const total = tickets.length;
      return { ...cur, tickets, stats: { ...cur.stats, audit_verified, remaining: total - audit_verified } };
    });
  }, []);

  // Zeigt Toast für Peer-Scan (nur, wenn nicht der eigene Scan)
  const showPeerToast = React.useCallback((ev) => {
    if (ev.result === "ok") {
      toast(`📡 Peer-Scan · Reihe ${ev.row} · Platz ${ev.seat}${ev.by_name ? " · " + ev.by_name : ""}`, {
        duration: 1500,
      });
    }
  }, []);

  // WebSocket-Verbindung pro Vorstellung
  useEffect(() => {
    if (!selectedId) return () => {};
    // Alte Verbindung schließen
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
    }
    setWsStatus("connecting");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^https/i, "wss").replace(/^http/i, "ws")
      + `/api/audit/sync/${selectedId}`
      + `?token=${encodeURIComponent(localStorage.getItem("mh_token") || "")}`;
    let ws;
    let heartbeat;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
      } catch { setWsStatus("fallback"); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setWsStatus("live");
        heartbeat = setInterval(() => { try { ws.send("ping"); } catch { /* ignore */ } }, 25000);
      };
      ws.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data);
          if (ev.type === "hello") setPeers(ev.peers || 1);
          else if (ev.type === "scan") { applyLiveScan(ev); showPeerToast(ev); }
          else if (ev.type === "reset") reload();
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setWsStatus("fallback");
        if (heartbeat) clearInterval(heartbeat);
        if (!closed) reconnectTimer = setTimeout(connect, 4000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();

    return () => {
      closed = true;
      if (heartbeat) clearInterval(heartbeat);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* ignore */ } }
      wsRef.current = null;
    };
  }, [selectedId, applyLiveScan, showPeerToast, reload]);

  // Fallback-Polling nur, wenn WebSocket nicht "live" — alle 8 Sekunden
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!selectedId) return;
    if (wsStatus === "live") return;   // WS reicht
    pollRef.current = setInterval(reload, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, wsStatus, reload]);

  useEffect(() => {
    api.get("/showtimes?upcoming=true").then((r) => {
      const list = (r.data || []).slice(0, 30);
      setShowtimes(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    }).catch(() => {});
  }, []);

  useEffect(reload, [reload]);

  const doScan = async (token) => {
    if (!token || !selectedId) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/showtimes/audit/scan", { showtime_id: selectedId, qr_token: token });
      setLastScan(r);
      if (r.result === "ok") toast.success(`✓ Reihe ${r.ticket.row} · Platz ${r.ticket.seat} bestätigt`);
      else if (r.result === "already") toast.warning("Bereits bestätigt");
      else if (r.result === "storniert") toast.error("Storniertes Ticket");
      // Optimistisches Update — Broadcast kommt gleich; kein reload nötig, wenn WS live
      if (r.ticket) applyLiveScan({ ticket_id: r.ticket.id, audit_verified_at: r.ticket.audit_verified_at });
      if (wsStatus !== "live") reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ticket nicht gefunden");
      setLastScan({ result: "invalid" });
    } finally { setBusy(false); }
  };

  const startCam = async () => {
    setScanning(true);
    try {
      const q = new Html5Qrcode("audit-qr");
      scannerRef.current = q;
      await q.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        (decoded) => {
          let token = decoded;
          try {
            const u = new URL(decoded);
            const parts = u.pathname.split("/").filter(Boolean);
            token = parts[parts.length - 1] || decoded;
          } catch { /* not a URL */ }
          doScan(token);
        },
        () => { /* ignore */ },
      );
    } catch { toast.error("Kamera nicht verfügbar"); setScanning(false); }
  };
  const stopCam = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* ignore */ }
    setScanning(false);
  };
  useEffect(() => () => { stopCam(); }, []);

  const resetAll = async () => {
    if (!window.confirm("Wirklich alle Bestätigungen für diese Vorstellung zurücksetzen?")) return;
    try { await api.post(`/admin/showtimes/${selectedId}/audit/reset`); toast.success("Zurückgesetzt"); reload(); }
    catch { toast.error("Fehler"); }
  };

  const grid = useMemo(() => {
    if (!data) return {};
    const byRow = {};
    for (const t of data.tickets) {
      const row = t.row || "?";
      byRow[row] = byRow[row] || [];
      byRow[row].push(t);
    }
    for (const r in byRow) byRow[r].sort((a, b) => (Number(a.seat) || 0) - (Number(b.seat) || 0));
    return byRow;
  }, [data]);

  if (!showtimes.length) {
    return <div className="text-[#A8A398] text-center py-20">
      <ShieldCheck size={40} className="mx-auto mb-3 text-[#575148]" />
      Keine anstehenden Vorstellungen zum Kontrollieren.
    </div>;
  }
  if (!data) return <div className="text-[#A8A398] text-center py-20">Lade …</div>;

  const st = data.showtime, stats = data.stats;
  const percent = stats.total ? Math.round(stats.audit_verified / stats.total * 100) : 0;

  const wsPill = wsStatus === "live"
    ? { color: "#D4AF37", bg: "bg-[#D4AF37]/10 border-[#D4AF37]/40", label: `LIVE · ${peers} Handy${peers === 1 ? "" : "s"}` }
    : wsStatus === "connecting"
    ? { color: "#EEDC82", bg: "bg-[#8B7328]/10 border-[#8B7328]/40", label: "Verbinde …" }
    : { color: "#A8A398", bg: "bg-[#0F0F0F] border-[#2A2A2A]", label: "Polling (8s)" };

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="saal-kontrolle-page">
      <div className="flex items-center gap-3">
        <ShieldCheck size={22} className="text-[#D4AF37]" />
        <div className="flex-1">
          <h1 className="font-heading text-2xl">Saal-Kontrolle</h1>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5">Jeden Sitz per Scan bestätigen</div>
        </div>
        <div className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-[9px] uppercase tracking-widest ${wsPill.bg}`}
          style={{ color: wsPill.color }} data-testid="audit-ws-status" title={`WS-Status: ${wsStatus}`}>
          <Radio size={10} className={wsStatus === "live" ? "animate-pulse" : ""} /> {wsPill.label}
        </div>
      </div>

      {/* Vorstellungs-Auswahl */}
      <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="audit-showtime-select"
        className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
        {showtimes.map((s) => (
          <option key={s.id} value={s.id}>
            {new Date(s.date).toLocaleDateString("de-DE")} · {s.start_time} · {s.hall} · {s.movie_title}
          </option>
        ))}
      </select>

      {/* Progress + Stats */}
      <div className="card-lux p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div><span className="text-[#EEDC82]">{st.movie_title}</span> · <span className="text-[#A8A398]">{st.hall}</span></div>
          <button onClick={reload} className="text-xs text-[#A8A398] hover:text-[#D4AF37]"><RefreshCw size={12} className="inline" /></button>
        </div>
        <div className="h-3 bg-[#050505] border border-[#1A1A1A] rounded overflow-hidden">
          <div style={{ width: `${percent}%`, background: "linear-gradient(90deg,#8B7328,#D4AF37,#EEDC82)" }} className="h-full transitions-safe" />
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><div className="font-heading text-2xl text-[#F5F0E6]" data-testid="audit-total">{stats.total}</div><div className="text-[9px] uppercase tracking-widest text-[#A8A398]">Verkauft</div></div>
          <div><div className="font-heading text-2xl text-[#D4AF37]" data-testid="audit-verified">{stats.audit_verified}</div><div className="text-[9px] uppercase tracking-widest text-[#A8A398]">Kontrolliert</div></div>
          <div><div className="font-heading text-2xl text-[#EE9CA1]" data-testid="audit-remaining">{stats.remaining}</div><div className="text-[9px] uppercase tracking-widest text-[#A8A398]">Fehlend</div></div>
        </div>
      </div>

      {/* Scanner */}
      <div className="card-lux p-4">
        <div id="audit-qr" className="w-full max-w-sm mx-auto rounded overflow-hidden bg-black" style={{ minHeight: scanning ? 240 : 0 }} />
        <div className="flex gap-2 mt-3">
          {!scanning ? (
            <button onClick={startCam} data-testid="audit-cam-start"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-4 py-3 rounded font-semibold text-sm">
              <Camera size={14} /> Kamera starten
            </button>
          ) : (
            <button onClick={stopCam}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#7A1921] hover:bg-[#5B1319] text-white px-4 py-3 rounded font-semibold text-sm">
              <CameraOff size={14} /> Stop
            </button>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Manuell: Ticket-Nr / QR / z.B. C-7"
            className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
            data-testid="audit-manual-input" onKeyDown={(e) => e.key === "Enter" && (doScan(manual.trim()), setManual(""))} />
          <button onClick={() => { doScan(manual.trim()); setManual(""); }} disabled={busy || !manual}
            className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded text-xs uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40" data-testid="audit-manual-btn">
            Prüfen
          </button>
        </div>
        {lastScan && lastScan.ticket && (
          <div className={`mt-3 p-2 rounded text-sm ${lastScan.result === "ok" ? "bg-[#D4AF37]/10 border border-[#D4AF37]/40" : "bg-[#5B1319]/20 border border-[#5B1319]/40"}`}>
            {lastScan.result === "ok" ? <CheckCircle2 size={14} className="inline mr-1 text-[#D4AF37]" /> : <AlertTriangle size={14} className="inline mr-1 text-[#EE9CA1]" />}
            Reihe {lastScan.ticket.row} · Platz {lastScan.ticket.seat} · {lastScan.ticket.guest_name || "—"}
          </div>
        )}
      </div>

      {/* Sitzplan */}
      <div className="card-lux p-4">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mb-3 flex items-center justify-between">
          <span><Users size={11} className="inline mr-1" /> Alle verkauften Sitze</span>
          <button onClick={resetAll} className="text-[9px] text-[#575148] hover:text-[#EE9CA1] underline">Alle zurücksetzen</button>
        </div>
        <div className="space-y-2">
          {Object.keys(grid).sort().map((row) => (
            <div key={row} className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-[#575148] w-6 shrink-0">{row}</div>
              <div className="flex-1 flex flex-wrap gap-1.5">
                {grid[row].map((t) => {
                  const ok = !!t.audit_verified_at;
                  const scanned = !!t.scanned_at;
                  const cancelled = t.status === "storniert" || t.status === "cancelled";
                  const label = String(t.seat).padStart(2, "0");
                  const cls = cancelled ? "border-[#2A2A2A] text-[#575148] line-through"
                    : ok ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#EEDC82]"
                    : scanned ? "bg-[#8B7328]/10 border-[#8B7328]/60 text-[#A8A398]"
                    : "bg-[#5B1319]/20 border-[#5B1319]/60 text-[#EE9CA1] animate-pulse";
                  return (
                    <button key={t.id} onClick={() => doScan(t.qr_token)}
                      title={`${t.guest_name || ""} · ${ok ? "kontrolliert" : scanned ? "eingelassen" : "noch nicht gesehen"}`}
                      data-testid={`audit-seat-${t.row}-${t.seat}`}
                      className={`w-9 h-9 border rounded flex items-center justify-center text-[10px] font-mono transitions-safe ${cls}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-4 text-[9px] uppercase tracking-widest text-[#575148]">
          <span><span className="inline-block w-3 h-3 rounded bg-[#D4AF37]/20 border border-[#D4AF37] mr-1 align-middle" /> Kontrolliert</span>
          <span><span className="inline-block w-3 h-3 rounded bg-[#8B7328]/10 border border-[#8B7328]/60 mr-1 align-middle" /> Nur eingelassen</span>
          <span><span className="inline-block w-3 h-3 rounded bg-[#5B1319]/20 border border-[#5B1319]/60 mr-1 align-middle" /> Fehlt</span>
        </div>
      </div>
    </div>
  );
}
